import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { listProductosAdmin, crearProductoCatalogo, actualizarProducto } from '../../services/data';
import { formatDateTime } from '../../utils/dates';
import '../finanzas/ui.css';
import '../proveedores/Proveedores.css';
import FondoModal from '../common/FondoModal';

// Catálogo maestro de productos (global para todas las taquerías) usado en Compras y Salidas entre taquerías.
// NO es inventario: sin existencias, costos ni movimientos. No se eliminan: se desactivan (los históricos conservan su nombre).
export default function Productos() {
  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [buscar, setBuscar] = useState('');
  const [verInactivos, setVerInactivos] = useState(false);
  const [form, setForm] = useState(null); // null | {} (nuevo) | producto
  const [ver, setVer] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try { setLista(await listProductosAdmin('', true)); } catch { setLista([]); } finally { setCargando(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    return lista.filter((p) => (verInactivos || p.activo) && (!q || [p.nombre, p.descripcion].some((x) => String(x || '').toLowerCase().includes(q))));
  }, [lista, buscar, verInactivos]);

  const cambiarEstado = async (p) => {
    const desactivar = p.activo;
    const ok = await Swal.fire({ title: desactivar ? 'Desactivar producto' : 'Reactivar producto', icon: 'question', showCancelButton: true,
      text: desactivar ? `${p.nombre} dejará de aparecer en compras y salidas nuevas. Los registros históricos lo conservan.`
        : `${p.nombre} vuelve a estar disponible en compras y salidas.`, confirmButtonText: desactivar ? 'Desactivar' : 'Reactivar', cancelButtonText: 'Cancelar' });
    if (!ok.isConfirmed) return;
    try { await actualizarProducto(p.id, { nombre: p.nombre, descripcion: p.descripcion, activo: !p.activo }); await cargar(); }
    catch (e) { Swal.fire('No se pudo actualizar', e.message || '', 'error'); }
  };

  return (
    <div className="prov-shell">
      <header className="prov-header">
        <div><h1>Productos</h1><p style={{ margin: 0, color: 'var(--ink-muted)' }}>Catálogo de productos utilizados en compras y movimientos operativos.</p></div>
        <div className="prov-actions"><button className="fx-btn fx-btn--primary" onClick={() => setForm({})}>+ Crear producto</button></div>
      </header>
      <div className="prov-filtros">
        <input type="search" placeholder="Buscar por nombre o descripción" value={buscar} onChange={(e) => setBuscar(e.target.value)} aria-label="Buscar producto" />
        <label><input type="checkbox" checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} /> Mostrar desactivados</label>
      </div>
      <div className="fx-card">
        {cargando ? <p className="fx-empty">Cargando…</p> : visibles.length ? (
          <table className="fx-table fx-table--stack">
            <thead><tr><th>Producto</th><th>Descripción</th><th>Estado</th><th>Acciones</th></tr></thead>
            <tbody>{visibles.map((p) => (
              <tr key={p.id} className={p.activo ? '' : 'prov-inactivo'}>
                <td data-label="Producto"><b>{p.nombre}</b></td>
                <td data-label="Descripción">{p.descripcion || '—'}</td>
                <td data-label="Estado"><span className={`fx-badge fx-badge--${p.activo ? 'completado' : 'proximo'}`}>{p.activo ? 'Activo' : 'Desactivado'}</span></td>
                <td data-label="Acciones"><div className="prov-row-actions">
                  <button className="fx-link" onClick={() => setVer(p)}>Ver</button>
                  <button className="fx-link" onClick={() => setForm(p)}>Editar</button>
                  <button className="fx-link" style={p.activo ? { color: 'var(--danger)' } : undefined} onClick={() => cambiarEstado(p)}>{p.activo ? 'Desactivar' : 'Reactivar'}</button>
                </div></td>
              </tr>))}
            </tbody>
          </table>
        ) : <p className="fx-empty">{lista.length ? 'Sin resultados.' : 'Aún no hay productos. Crea el primero con “+ Crear producto”.'}</p>}
      </div>

      {ver && <FondoModal onClose={() => setVer(null)}>
        <section className="fx-modal fx-modal--sm" role="dialog" aria-modal="true" aria-label="Producto" onMouseDown={(e) => e.stopPropagation()}>
          <header><div><h2>{ver.nombre}</h2><p>{ver.activo ? 'Activo' : 'Desactivado'}</p></div><button type="button" aria-label="Cerrar" onClick={() => setVer(null)}>×</button></header>
          <dl className="cp-datos">
            <div><dt>Descripción</dt><dd>{ver.descripcion || '—'}</dd></div>
            <div><dt>Usado en registros</dt><dd>{ver.usos || 0}</dd></div>
            <div><dt>Creado</dt><dd>{ver.creadoPor || '—'}{ver.creadoAt ? ` · ${formatDateTime(ver.creadoAt)}` : ''}</dd></div>
            {ver.modificadoAt && <div><dt>Última modificación</dt><dd>{ver.modificadoPor || '—'} · {formatDateTime(ver.modificadoAt)}</dd></div>}
          </dl>
          <div className="fx-actions"><button type="button" className="fx-btn fx-btn--primary" onClick={() => { setForm(ver); setVer(null); }}>Editar</button></div>
        </section>
      </FondoModal>}
      {form && <ProductoFormModal producto={form.id ? form : null} onClose={() => setForm(null)} onSaved={() => { setForm(null); cargar(); }} />}
    </div>
  );
}

function ProductoFormModal({ producto, onClose, onSaved }) {
  const [f, setF] = useState({ nombre: producto?.nombre || '', descripcion: producto?.descripcion || '', activo: producto ? producto.activo : true });
  const [busy, setBusy] = useState(false);
  const guardar = async (e) => {
    e.preventDefault();
    if (!f.nombre.trim()) return Swal.fire('Producto', 'Indica el nombre del producto.', 'warning');
    setBusy(true);
    try {
      if (producto) await actualizarProducto(producto.id, f); else await crearProductoCatalogo(f);
      onSaved();
    } catch (err) { Swal.fire('No se pudo guardar', err.message || '', 'error'); }
    finally { setBusy(false); }
  };
  return (
    <FondoModal onClose={onClose}>
      <form className="fx-modal fx-modal--sm fx-compacto" role="dialog" aria-modal="true" aria-label={producto ? 'Editar producto' : 'Crear producto'} onSubmit={guardar} onMouseDown={(e) => e.stopPropagation()}>
        <header><div><h2>{producto ? 'Editar producto' : 'Crear producto'}</h2></div><button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
        <p className="fx-intro">Completa la información. Los campos marcados con * son obligatorios.</p>
        <div className="fx-form">
          <label className="full">Nombre <span className="fx-req" aria-hidden="true">*</span><input autoFocus required maxLength={120} value={f.nombre} placeholder="Ej.: Aguacate" onChange={(e) => setF({ ...f, nombre: e.target.value })} /></label>
          <label className="full">Descripción<textarea rows={2} maxLength={300} value={f.descripcion} placeholder="Producto utilizado para preparación de tacos"
            onChange={(e) => setF({ ...f, descripcion: e.target.value })} /></label>
          <label className="full fx-check"><input type="checkbox" checked={f.activo} onChange={(e) => setF({ ...f, activo: e.target.checked })} /> Producto activo</label>
          {producto && <small className="fx-help full">Cambiar el nombre no modifica compras ni salidas ya registradas: conservan el nombre con el que se guardaron.</small>}
        </div>
        <div className="fx-actions"><button type="button" className="fx-btn" onClick={onClose}>Cancelar</button>
          <button className="fx-btn fx-btn--primary" disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</button></div>
      </form>
    </FondoModal>
  );
}
